/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import SortedTableHead from "./SortedTableHead";
import Row from "./Row";
import AlertBox from "../Shared/AlertBox";
import PropTypes from "prop-types";
import {
  ALARM_SEVERITIES,
  getAlarmSeverityRank,
  normalizeAlarmSeverity,
} from "../../lib/AlarmColors";

/**
 * Validates if sorting will be ascendent or descendent
 *
 * @param {a}       Object of the array in the map loop
 * @param {b}       Next object of the array
 * @param {orderBy} Field to order
 */
export function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

/**
 * Returns the descending type
 *
 * @param {order}   asc or desc
 * @param {orderBy} Field to order
 */
export function getComparator(order, orderBy) {
  return order === "asc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Creates a new array with sorted data
 *
 * @param {array}      List of sites
 * @param {comparator} Field to order
 */
export function stableSort(array, comparator) {
  const stabilizedThis = array.map((elem, index) => [elem, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((elem) => elem[0]);
}

const CollapsibleTable = ({ sites }) => {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("name");
  const [alertData, setAlertData] = useState(null);

  /**
   * Updates the type of order and the field
   *
   * @param {event}
   * @param {property} Field to order
   */
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sitesProcessed = sites?.map((item) => {
    const total_power = item.power_data.total_power;

    // Weight by worst severity so Critical sorts above High/Medium/Low.
    const worst = ALARM_SEVERITIES.find((severity) =>
      item.alarms?.some(
        (alarm) => normalizeAlarmSeverity(alarm.severity) === severity && alarm.count > 0,
      ),
    );
    const worstCount = worst
      ? (item.alarms.find((alarm) => normalizeAlarmSeverity(alarm.severity) === worst)?.count ?? 0)
      : 0;
    const count = worst
      ? (ALARM_SEVERITIES.length - getAlarmSeverityRank(worst)) * 1000 + worstCount
      : 0;

    return {
      qtyAlarms: count,
      total_power: total_power,
      weather_temperature: item.weather_data?.temperature_2m || -999, // Use -999 as a default value for sorting when no temperature data
      ...item,
    };
  });

  // if we have "data" here from React Query, we can assume it is fresh data and use it
  if (sitesProcessed) {
    return (
      <TableContainer>
        {alertData && (
          <AlertBox
            type={alertData.type}
            message={alertData.message}
            openAlert={alertData.openAlert}
            clearAlert={() => setAlertData(null)}
          />
        )}
        <Table stickyHeader={true} aria-label="sticky table">
          <SortedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
          <TableBody>
            {stableSort(sitesProcessed, getComparator(order, orderBy)).map((site, _id) => (
              <Row key={site.name} row={site} setAlertData={setAlertData} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
};

CollapsibleTable.propTypes = {
  sites: PropTypes.array,
};

export default CollapsibleTable;
